import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, generatePath, useHistory } from 'react-router-dom';
// @ts-ignore
import { Autosuggest } from '@els/els-react--autosuggest';
// @ts-ignore
import { Button, ButtonWithIcon } from '@els/els-react--button';
// @ts-ignore
import { Icon } from '@els/els-react--icon';
// @ts-ignore
import { Loader } from '@els/els-react--loader';
import { getCombinedAutoSuggest } from '../../features/search/search-thunk';
import { resetAutoSuggestData } from '../../features/search/search-slice';
import {
    selectRecentViewedSkills,
    selectPEAutoSuggestData,
    selectPEAutoSuggestStatus,
    selectRecentViewedSkillsAsyncStatus,
    selectKeywordAutoSuggestData,
    selectKeywordAutoSuggestStatus,
    selectHSearchSkillAsyncStatus,
} from '../../features/search/search-selector';

import {
    isPending,
    hasSucceeded,
    hasFailed,
    isComplete,
} from '../../../libs/utils/constants/async-status';
import Routes from '../../constants/routes';
import './auto-suggest.scss';
import AriaLiveAnnouncer from '../aria-live-announcer/aria-live-announcer';

const AutoSuggestComponent = ({
    onHandleSearchClick,
    keyword,
    allActiveSkillsDetails,
    contentTypeLicensedByClient,
    contentTypeLicensedAsyncStatus,
    saveSearchState,
    contentType,
}: any) => {
    const baseClassName = 'auto-suggest';
    const autoSuggestRef = useRef<any>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const suppressNextKeyUpRef = useRef(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const peAutoSuggestData = useSelector(selectPEAutoSuggestData);
    const peAutoSuggestStatus = useSelector(selectPEAutoSuggestStatus);
    const {
        innerWidth: width,
        location: { pathname },
    } = window;
    const checkSearchRoute = pathname.includes('search');
    const recentViewedSkillsData: any = useSelector(selectRecentViewedSkills);
    const recentViewedSkillsAsyncStatus = useSelector(
        selectRecentViewedSkillsAsyncStatus
    );
    const [searchText, setSearchText] = React.useState(keyword);
    const [peAutoSuggestStateData, setPEAutoSuggestStateData] = React.useState<
        any[]
    >([]);
    const [activeSkills, setActiveSkills] = React.useState<any[]>([]);
    const { ecrId } = useParams<{ ecrId: string }>();
    const [shouldShowRecentViewedSkills, setShouldShowRecentViewedSkills] =
        React.useState(false);
    const [showAutoSuggestFlyout, setShowAutoSuggestFlyout] =
        React.useState(true);
    const keywordAutoSuggestData = useSelector(selectKeywordAutoSuggestData);
    const keywordAutoSuggestStatus = useSelector(
        selectKeywordAutoSuggestStatus
    );
    const hSearchAsyncStatus = useSelector(selectHSearchSkillAsyncStatus);
    const [keywordsStateData, setKeywordsStateData] = React.useState<any[]>([]);
    const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
    const [totalFlyoutItems, setTotalFlyoutItems] = React.useState<number>(0);
    const [alert, setAlert] = React.useState<string>('');
    const [lastStatus, setLastStatus] = React.useState<{
        peAuto: string | null;
        keywordAuto: string | null;
    }>({ peAuto: null, keywordAuto: null });

    // Helper function to get the input element
    const getInputElement = (): HTMLInputElement | null =>
        inputRef.current ||
        (document.querySelector(
            '#autoSearchWrapper input'
        ) as HTMLInputElement);

    useEffect(() => {
        setPEAutoSuggestStateData(
            width < 600
                ? peAutoSuggestData.slice(0, 3)
                : peAutoSuggestData.slice(0, 5)
        );
        setKeywordsStateData(
            width < 600
                ? keywordAutoSuggestData.slice(0, 3)
                : keywordAutoSuggestData.slice(0, 5)
        );
    }, [peAutoSuggestData, keywordAutoSuggestData, width]);
    useEffect(() => {
        if (searchText.length >= 3) {
            const matchingSkills = allActiveSkillsDetails.filter((item: any) =>
                item.name.toLowerCase().includes(searchText.toLowerCase())
            );
            matchingSkills.sort((a: any, b: any) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            );
            setActiveSkills(
                width < 600
                    ? matchingSkills.slice(0, 3)
                    : matchingSkills.slice(0, 5)
            );
        }
    }, [allActiveSkillsDetails, searchText, width]);

    useEffect(() => {
        const nextSearchText = keyword || '';
        setShowAutoSuggestFlyout(false);
        setSearchText(nextSearchText);

        const inputElement = getInputElement(); // Use the helper function
        if (inputElement) {
            autoSuggestRef?.current?.setTextInputValue(nextSearchText);
        }
    }, [keyword, contentType]);

    const handleFocus = () => {
        dispatch(resetAutoSuggestData());
        setFocusedIndex(-1);
        if (searchText.length >= 3) {
            setShouldShowRecentViewedSkills(false);
        }
        if (searchText.length <= 2) {
            setShouldShowRecentViewedSkills(true);
        }
    };

    const handleBlur = () => {
        setTimeout(() => {
            setShouldShowRecentViewedSkills(false);
            setShowAutoSuggestFlyout(false);
            setFocusedIndex(-1);
        }, 225);
    };

    const onSelectResultHandler = (event: any) => {
        if (event !== undefined && focusedIndex < 0) {
            setSearchText(event);
            onHandleSearchClick(event);
        }
    };

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const debounce =
        (func: Function, delay: number) =>
        (...args: any[]) => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => func(...args), delay);
        };

    const onSearchHandler = debounce((event: any) => {
        setSearchText(event);
        setFocusedIndex(-1);
        if (event.length <= 2) {
            setShouldShowRecentViewedSkills(true);
        }
        if (event.length >= 3) {
            setShowAutoSuggestFlyout(true);
            dispatch(
                getCombinedAutoSuggest({
                    keyword: event,
                    nursingSkillsClientId: ecrId,
                })
            );
            setShouldShowRecentViewedSkills(false);
        }
    }, 300);

    const resetSearchText = () => {
        dispatch(resetAutoSuggestData());
        const inputElement = getInputElement(); // Use the helper function
        if (inputElement) {
            autoSuggestRef?.current?.setTextInputValue('');
        }
        setSearchText('');
        // Refocus the input (no scroll jump) and suppress the activating key's trailing keyup, which would otherwise land here and trigger a search.
        suppressNextKeyUpRef.current = true;
        inputElement?.focus({ preventScroll: true });
        setTimeout(() => {
            suppressNextKeyUpRef.current = false;
        }, 300);
        setTimeout(() => {
            setAlert('Search input cleared.');
        }, 300);
    };

    const onClickSearchIconHandler = () => {
        dispatch(resetAutoSuggestData());
        setShowAutoSuggestFlyout(false);
        onHandleSearchClick(searchText);
    };

    // Helper function to find and click the focused element
    const clickFocusedElement = (index: number) => {
        const focusedDiv = document.getElementById(`flyout-button-${index}`);
        if (focusedDiv) {
            const focusedButton = focusedDiv.querySelector('button');
            if (focusedButton) {
                focusedButton.click();
                // Reset states after selection
                dispatch(resetAutoSuggestData());
                setShouldShowRecentViewedSkills(false);
                setShowAutoSuggestFlyout(false);
                setFocusedIndex(-1);
            }
        }
    };

    const handleKeyDown = (event: any) => {
        // Ignore the clear button's trailing keyup, redirected here since focus moved mid-keypress.
        if (suppressNextKeyUpRef.current) {
            suppressNextKeyUpRef.current = false;
            return;
        }
        // Handle arrow key navigation
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (totalFlyoutItems > 0) {
                if (event.key === 'ArrowDown') {
                    setFocusedIndex((prev) =>
                        prev < totalFlyoutItems - 1 ? prev + 1 : 0
                    );
                } else {
                    setFocusedIndex((prev) =>
                        prev > 0 ? prev - 1 : totalFlyoutItems - 1
                    );
                }
            }
            return; // Exit early to prevent other handlers
        }
        // Handle Escape key to close the flyout
        if (event.key === 'Escape') {
            event.preventDefault();
            handleBlur();
        }
        // Handle Enter key for focused item
        if (
            event.key === 'Enter' &&
            focusedIndex >= 0 &&
            totalFlyoutItems > 0
        ) {
            event.preventDefault();
            clickFocusedElement(focusedIndex);
            return; // Exit early to prevent other handlers
        }
        // Handle Space key for focused item
        if (event.key === ' ' && focusedIndex >= 0 && totalFlyoutItems > 0) {
            event.preventDefault();
            clickFocusedElement(focusedIndex);
            return; // Exit early to prevent other handlers
        }
        // Handle Enter key for search when no item is focused
        if (event.key === 'Enter' && focusedIndex < 0) {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            event.preventDefault();
            dispatch(resetAutoSuggestData());
            setShouldShowRecentViewedSkills(false);
            setShowAutoSuggestFlyout(false);
            setFocusedIndex(-1);
            if (hasSucceeded(hSearchAsyncStatus)) {
                onHandleSearchClick(searchText);
            }
        }
        if (
            event?.keyCode === 8 &&
            event?.target?.value === '' &&
            checkSearchRoute
        ) {
            setSearchText('');
        }
    };

    const escapeRegExp = (str: string) =>
        str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const matchHighlightWord = (suggestedTitle: string, query: string) => {
        if (!suggestedTitle || !query) return [];
        const regex = new RegExp(`(${escapeRegExp(query)})`, 'i');
        return suggestedTitle.split(regex).map((part) => ({
            text: part,
            isHighlighted: regex.test(part),
        }));
    };

    const renderSuggestion = (suggestedTitle: string, query: string) => (
        <span>
            {matchHighlightWord(suggestedTitle, query).map((part, id) => (
                <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${suggestedTitle}-${id}`}
                    className={
                        part.isHighlighted
                            ? `${baseClassName}__flyout-item--bold`
                            : undefined
                    }
                >
                    {part.text}
                </span>
            ))}
        </span>
    );

    const handleRedirectToPEContentDetails = (
        eid: string,
        languageCode: string,
        spanishLanguageId: string
    ) => {
        dispatch(resetAutoSuggestData());
        if (checkSearchRoute) {
            saveSearchState();
        }
        const redirectLink = `${generatePath(Routes.PATIENT_EDUCATION, {
            ecrId,
        })}`;
        sessionStorage.setItem('peQueryParamsEid', eid);
        sessionStorage.setItem('peQueryParamsLanguageCode', languageCode);
        sessionStorage.setItem('peQueryParamsSpanishId', spanishLanguageId);
        history.push(`${redirectLink}`);
        window.scrollTo(0, 0);
    };

    const handleOnSuggestionSkillClick = (id: any) => {
        dispatch(resetAutoSuggestData());
        if (checkSearchRoute) {
            saveSearchState();
        }
        const redirectLink = generatePath(Routes.SKILL_LAUNCH, {
            id,
            ecrId,
        });
        history.push(`${redirectLink}/`);
        window.scrollTo(0, 0);
    };

    const handleOnClickKeyword = (id: any) => {
        dispatch(resetAutoSuggestData());
        if (checkSearchRoute) {
            saveSearchState();
        }
        setSearchText(id);
        onHandleSearchClick(id);
    };

    const handleKeyPress = (
        event: React.KeyboardEvent<HTMLDivElement>,
        callback: () => void
    ) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            callback();
        }
    };

    const showFlyoutConditions =
        searchText.length > 2 &&
        (isPending(peAutoSuggestStatus) ||
            ((hasSucceeded(peAutoSuggestStatus) ||
                hasFailed(peAutoSuggestStatus)) &&
                peAutoSuggestStateData.length > 0) ||
            isPending(keywordAutoSuggestStatus) ||
            ((hasSucceeded(keywordAutoSuggestStatus) ||
                hasFailed(keywordAutoSuggestStatus)) &&
                keywordAutoSuggestData.length > 0) ||
            activeSkills.length > 0);

    const recentViewedSkillsConditions =
        (searchText.length <= 2 &&
            searchText.length > 0 &&
            (isPending(recentViewedSkillsAsyncStatus) ||
                ((hasSucceeded(recentViewedSkillsAsyncStatus) ||
                    hasFailed(recentViewedSkillsAsyncStatus)) &&
                    recentViewedSkillsData.length > 0))) ||
        shouldShowRecentViewedSkills;

    let autoSuggetsResultProps: string[] = [];

    // Helper function to get the button index based on the current flyout state
    const getButtonIndex = (
        section: 'keyword' | 'skill' | 'pe' | 'recent',
        itemIndex: number
    ): number => {
        let index = 0;

        if (showFlyoutConditions) {
            // Keywords come first
            if (section === 'keyword') return itemIndex;
            index += keywordsStateData.length;

            // Skills come second
            if (section === 'skill') return index + itemIndex;
            index += activeSkills.length;

            // PE comes last
            if (section === 'pe') return index + itemIndex;
        } else if (recentViewedSkillsConditions) {
            // Only recent skills in this case
            if (section === 'recent') return itemIndex;
        }

        return index;
    };

    // Effect to focus on the selected flyout item
    const prevFocusedIndex = useRef<number | null>(null); // Move useRef to the top level

    useEffect(() => {
        // Update the input's aria-activedescendant to point to the focused option
        // Try to find the input element using querySelector as a fallback
        const inputElement = getInputElement(); // Use the helper function

        if (focusedIndex >= 0 && totalFlyoutItems > 0) {
            const focusedDiv = document.getElementById(
                `flyout-button-${focusedIndex}`
            );
            if (focusedDiv) {
                // Don't actually focus the div to avoid interfering with input focus
                focusedDiv.setAttribute('data-keyboard-focused', 'true');
                focusedDiv.setAttribute('aria-selected', 'true');

                // Set aria-activedescendant on the input to point to the focused option
                if (inputElement) {
                    inputElement.setAttribute(
                        'aria-activedescendant',
                        `flyout-button-${focusedIndex}`
                    );
                }

                // Remove focus from the previously focused element
                if (
                    prevFocusedIndex.current !== null &&
                    prevFocusedIndex.current !== focusedIndex
                ) {
                    const prevDiv = document.getElementById(
                        `flyout-button-${prevFocusedIndex.current}`
                    );
                    if (prevDiv) {
                        prevDiv.removeAttribute('data-keyboard-focused');
                        prevDiv.setAttribute('aria-selected', 'false');
                    }
                }

                // Update the previous focused index
                prevFocusedIndex.current = focusedIndex;
            }
        } else {
            // Remove aria-activedescendant when no item is focused
            if (inputElement) {
                inputElement.removeAttribute('aria-activedescendant');
            }

            // Remove all focus indicators when no item is focused
            for (let i = 0; i < totalFlyoutItems; i += 1) {
                const div = document.getElementById(`flyout-button-${i}`);
                if (div) {
                    div.removeAttribute('data-keyboard-focused');
                    div.setAttribute('aria-selected', 'false');
                }
            }
        }
    }, [focusedIndex, totalFlyoutItems]);

    useEffect(() => {
        const keywordCount = keywordsStateData.length;
        const skillCount = activeSkills.length;
        const peCount = peAutoSuggestStateData.length;
        const recentSkillsCount = recentViewedSkillsData.length;
        if (showFlyoutConditions) {
            const resultCount = keywordCount + skillCount + peCount;
            setTotalFlyoutItems(resultCount);
        } else if (recentViewedSkillsConditions) {
            setTotalFlyoutItems(recentSkillsCount);
        } else {
            setTotalFlyoutItems(0);
            setFocusedIndex(-1);
        }
    }, [
        keywordsStateData,
        activeSkills,
        peAutoSuggestStateData,
        recentViewedSkillsData,
        showFlyoutConditions,
        recentViewedSkillsConditions,
    ]);

    useEffect(() => {
        const keywordCount =
            width < 600
                ? keywordAutoSuggestData.slice(0, 3).length
                : keywordAutoSuggestData.slice(0, 5).length;
        const skillCount = activeSkills.length;
        const peCount =
            width < 600
                ? peAutoSuggestData.slice(0, 3).length
                : peAutoSuggestData.slice(0, 5).length;
        const statusComplete =
            isComplete(peAutoSuggestStatus) &&
            isComplete(keywordAutoSuggestStatus);
        const newStatus =
            lastStatus.peAuto !== peAutoSuggestStatus ||
            lastStatus.keywordAuto !== keywordAutoSuggestStatus;
        if (showFlyoutConditions && statusComplete && newStatus) {
            let resultCount = keywordCount + skillCount;
            if (
                hasSucceeded(contentTypeLicensedAsyncStatus) &&
                contentTypeLicensedByClient?.PE
            ) {
                resultCount += peCount;
            }
            setTimeout(() => {
                setAlert(
                    resultCount > 0
                        ? `${resultCount} suggestions available, use up and down arrow keys to navigate.`
                        : `No suggestions found matching ${searchText}`
                );
            }, 300);
        } else if (recentViewedSkillsConditions) {
            const recentSkillsCount = recentViewedSkillsData.length;

            if (recentSkillsCount > 0 && searchText.trim().length === 0) {
                setTimeout(() => {
                    setAlert(
                        `${recentSkillsCount} recently viewed skills available, use up and down arrow keys to navigate.`
                    );
                }, 300);
            } else if (
                recentSkillsCount === 0 &&
                searchText.trim().length === 0
            ) {
                setTimeout(() => {
                    setAlert(
                        'Enter a keyword and press the Search button to view results'
                    );
                }, 300);
            }
        } else if (statusComplete && newStatus) {
            setTimeout(() => {
                setAlert(
                    searchText.length >= 3
                        ? `No results found matching ${searchText}`
                        : ''
                );
            }, 300);
        }
        setLastStatus({
            peAuto: peAutoSuggestStatus,
            keywordAuto: keywordAutoSuggestStatus,
        });
    }, [
        peAutoSuggestStatus,
        keywordAutoSuggestStatus,
        activeSkills,
        keywordsStateData,
        recentViewedSkillsConditions,
        recentViewedSkillsData.length,
        searchText,
        contentTypeLicensedAsyncStatus,
        contentTypeLicensedByClient,
    ]);

    if (recentViewedSkillsConditions && !showFlyoutConditions) {
        autoSuggetsResultProps = recentViewedSkillsData.map(
            (item: any) => item.name
        );
    } else if (showFlyoutConditions) {
        autoSuggetsResultProps = [
            ...peAutoSuggestStateData.map((item) => item.itemtitle),
            ...activeSkills.map((item) => item.name),
            ...keywordAutoSuggestData.map((item) => item),
        ];
    }
    return (
        <div className={`${baseClassName}__search-bar`}>
            <AriaLiveAnnouncer message={alert} setAlert={setAlert} />
            <Autosuggest
                textInputProps={{
                    label: '',
                    placeholder: 'Search',
                    value: searchText,
                    onFocus: handleFocus,
                    onBlur: handleBlur,
                    onKeyUp: handleKeyDown,
                    inputRef,
                }}
                className="auto-search"
                id="autoSearchWrapper"
                onChange={onSearchHandler}
                results={autoSuggetsResultProps}
                onSelectResult={onSelectResultHandler}
                onSelectSuggestion={onSelectResultHandler}
                ref={autoSuggestRef}
                shouldAlwaysChooseSuggestion={false}
                filterMatch={Autosuggest.FilterMatch}
                suggestionsFlyoutProps={{
                    ...(showFlyoutConditions && {
                        isOpen: showAutoSuggestFlyout,
                        flyout: (
                            <div
                                className={`${baseClassName}__flyout`}
                                role="listbox"
                            >
                                {isPending(peAutoSuggestStatus) && (
                                    <Loader
                                        isVisible
                                        isBlocking={false}
                                        className={`${baseClassName}__loader`}
                                    />
                                )}
                                {(hasSucceeded(peAutoSuggestStatus) ||
                                    hasFailed(peAutoSuggestStatus)) &&
                                    (hasSucceeded(keywordAutoSuggestStatus) ||
                                        hasFailed(
                                            keywordAutoSuggestStatus
                                        )) && (
                                        <>
                                            <div
                                                className={`${baseClassName}__flyout-keyword`}
                                            >
                                                {keywordsStateData.length >
                                                    0 && (
                                                    <h5
                                                        className={`${baseClassName}__flyout-header`}
                                                    >
                                                        SEARCH BY KEYWORD
                                                    </h5>
                                                )}
                                                {keywordsStateData.length > 0 &&
                                                    keywordsStateData.map(
                                                        (
                                                            item: any,
                                                            index: number
                                                        ) => (
                                                            <div
                                                                key={`${item}`}
                                                                id={`flyout-button-${getButtonIndex(
                                                                    'keyword',
                                                                    index
                                                                )}`}
                                                                role="option"
                                                                aria-label={`Search by keyword: ${item}`}
                                                                aria-selected="false"
                                                                className={`${baseClassName}__flyout-item`}
                                                            >
                                                                <Button
                                                                    type="link"
                                                                    onClick={() => {
                                                                        handleOnClickKeyword(
                                                                            item
                                                                        );
                                                                    }}
                                                                    tabIndex={
                                                                        -1
                                                                    }
                                                                >
                                                                    {renderSuggestion(
                                                                        item,
                                                                        searchText
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        )
                                                    )}
                                            </div>
                                            <div
                                                className={`${baseClassName}__flyout-skill`}
                                            >
                                                {activeSkills.length > 0 && (
                                                    <h5
                                                        className={`${baseClassName}__flyout-header`}
                                                    >
                                                        GO TO SKILL
                                                    </h5>
                                                )}
                                                {activeSkills.length > 0 &&
                                                    activeSkills.map(
                                                        (
                                                            item: any,
                                                            index: number
                                                        ) => (
                                                            <div
                                                                key={`${item.id}-${item.name}`}
                                                                id={`flyout-button-${getButtonIndex(
                                                                    'skill',
                                                                    index
                                                                )}`}
                                                                role="option"
                                                                aria-label={`Go to skill: ${item.name}`}
                                                                aria-selected="false"
                                                                className={`${baseClassName}__flyout-item`}
                                                            >
                                                                <Button
                                                                    type="link"
                                                                    onClick={() => {
                                                                        handleOnSuggestionSkillClick(
                                                                            item.id
                                                                        );
                                                                    }}
                                                                    tabIndex={
                                                                        -1
                                                                    }
                                                                >
                                                                    {renderSuggestion(
                                                                        item.name,
                                                                        searchText
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        )
                                                    )}
                                            </div>
                                            {hasSucceeded(
                                                contentTypeLicensedAsyncStatus
                                            ) &&
                                                contentTypeLicensedByClient?.PE && (
                                                    <div
                                                        className={`${baseClassName}__flyout-pe`}
                                                    >
                                                        {peAutoSuggestStateData.length >
                                                            0 && (
                                                            <h5
                                                                className={`${baseClassName}__flyout-header`}
                                                            >
                                                                GO TO PATIENT
                                                                EDUCATION
                                                            </h5>
                                                        )}
                                                        {peAutoSuggestStateData.length >
                                                            0 &&
                                                            peAutoSuggestStateData.map(
                                                                (
                                                                    item: any,
                                                                    index: number
                                                                ) => (
                                                                    <div
                                                                        key={`${item.eid}-${item.itemtitle}`}
                                                                        id={`flyout-button-${getButtonIndex(
                                                                            'pe',
                                                                            index
                                                                        )}`}
                                                                        role="option"
                                                                        aria-label={`Go to patient education: ${item.itemtitle}`}
                                                                        aria-selected="false"
                                                                        className={`${baseClassName}__flyout-item`}
                                                                    >
                                                                        <Button
                                                                            type="link"
                                                                            onClick={() => {
                                                                                handleRedirectToPEContentDetails(
                                                                                    item.eid,
                                                                                    'eng',
                                                                                    !item.altlang ||
                                                                                        !item.altlangeid
                                                                                        ? ''
                                                                                        : item
                                                                                              .altlangeid[
                                                                                              item.altlang.indexOf(
                                                                                                  'es'
                                                                                              )
                                                                                          ] ||
                                                                                              ''
                                                                                );
                                                                            }}
                                                                            tabIndex={
                                                                                -1
                                                                            }
                                                                        >
                                                                            {renderSuggestion(
                                                                                item.itemtitle,
                                                                                searchText
                                                                            )}
                                                                        </Button>
                                                                    </div>
                                                                )
                                                            )}
                                                    </div>
                                                )}
                                        </>
                                    )}
                            </div>
                        ),
                    }),
                    ...(recentViewedSkillsConditions &&
                        autoSuggetsResultProps && {
                            isOpen: shouldShowRecentViewedSkills,
                            flyout: (
                                <div
                                    className={`${baseClassName}__flyout`}
                                    role="listbox"
                                >
                                    {isPending(
                                        recentViewedSkillsAsyncStatus
                                    ) && (
                                        <Loader
                                            isVisible
                                            isBlocking
                                            className={`${baseClassName}__loader`}
                                        />
                                    )}
                                    {(hasSucceeded(
                                        recentViewedSkillsAsyncStatus
                                    ) ||
                                        hasFailed(
                                            recentViewedSkillsAsyncStatus
                                        )) && (
                                        <div
                                            className={`${baseClassName}__flyout-skill`}
                                        >
                                            {recentViewedSkillsData.length >
                                                0 && (
                                                <h5
                                                    className={`${baseClassName}__flyout-header`}
                                                >
                                                    RECENTLY VIEWED SKILLS
                                                </h5>
                                            )}
                                            {recentViewedSkillsData.length >
                                                0 &&
                                                recentViewedSkillsData.map(
                                                    (
                                                        item: {
                                                            id: string;
                                                            name: string;
                                                        },
                                                        index: number
                                                    ) => (
                                                        <div
                                                            key={`${item.id}-${item.name}`}
                                                            id={`flyout-button-${getButtonIndex(
                                                                'recent',
                                                                index
                                                            )}`}
                                                            role="option"
                                                            aria-label={`Recently viewed skill: ${item.name}`}
                                                            aria-selected="false"
                                                            className={`${baseClassName}__flyout-item`}
                                                        >
                                                            <ButtonWithIcon
                                                                sprite={
                                                                    Icon.Sprites
                                                                        .CLOCK
                                                                }
                                                                type="link"
                                                                onClick={() => {
                                                                    handleOnSuggestionSkillClick(
                                                                        item.id
                                                                    );
                                                                }}
                                                                tabIndex={-1}
                                                            >
                                                                {item.name}
                                                            </ButtonWithIcon>
                                                        </div>
                                                    )
                                                )}
                                        </div>
                                    )}
                                </div>
                            ),
                        }),
                }}
            />
            {(searchText.length > 0 || keyword.length > 0) && (
                <Icon
                    role="button"
                    a11y={{
                        name: 'Clear search input',
                    }}
                    className={`${baseClassName}__cancel-icon`}
                    textAlignment="middle"
                    size={Icon.Sizes.S}
                    sprite="FailedOutlineCircle"
                    aria-label="Clear search input"
                    name="Clear search input"
                    tabIndex={0}
                    onClick={resetSearchText}
                    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
                        handleKeyPress(event, resetSearchText)
                    }
                />
            )}
            <Icon
                role="button"
                a11y={{
                    name: 'search',
                }}
                textAlignment="bottom"
                size="s"
                className={`${baseClassName}__search-icon`}
                sprite={Icon.Sprites.SEARCH}
                aria-label="search"
                name="search"
                tabIndex={0}
                onClick={onClickSearchIconHandler}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
                    handleKeyPress(event, onClickSearchIconHandler)
                }
            />
        </div>
    );
};

export default AutoSuggestComponent;
